/**
 * Post-Add-to-Cart Upsell Modal (Toast)
 * Slide-in toast from bottom-right after ATC, showing complementary product recs.
 *
 * Pre-rendered by Liquid from product metafield data.
 * JS handles show/hide, quick-add via Cart API, and auto-dismiss.
 *
 * Events:
 * - Listens: `cart:updated` on document (triggers toast when source = 'cart-drawer')
 * - Dispatches: `cart:open` on document (opens cart drawer)
 * - Dispatches: `cart:updated` on document (after quick-add)
 *
 * @extends ABBaseComponent
 */
import { ABBaseComponent, defineComponent } from './ab-base-component.js';

class ABPostAtcModal extends ABBaseComponent {
  connectedCallback() {
    // Bail early if no rec cards inside
    this._recCards = this.$$('.ab-post-atc__rec-card');
    if (!this._recCards || this._recCards.length === 0) return;

    // State
    this._isVisible = false;
    this._autoDismissTimer = null;
    this._isAdding = false;
    this._dismissedByUser = false;
    this._shownThisSession = false;

    // Bind handlers
    this._onCartUpdated = this._onCartUpdated.bind(this);
    this._onCloseClick = this._onCloseClick.bind(this);
    this._onViewCartClick = this._onViewCartClick.bind(this);
    this._onRecAddClick = this._onRecAddClick.bind(this);
    this._onTransitionEnd = this._onTransitionEnd.bind(this);

    // Listen for cart updates (fired by cart drawer after ATC)
    document.addEventListener('cart:updated', this._onCartUpdated);

    // Close button
    const closeBtn = this.$('[data-post-atc-close]');
    if (closeBtn) closeBtn.addEventListener('click', this._onCloseClick);

    // View Cart button
    const viewCartBtn = this.$('[data-post-atc-view-cart]');
    if (viewCartBtn) viewCartBtn.addEventListener('click', this._onViewCartClick);

    // Quick-add buttons (delegated on recs container)
    const recsContainer = this.$('.ab-post-atc__recs');
    if (recsContainer) recsContainer.addEventListener('click', this._onRecAddClick);

    // Transition end for cleanup
    this.addEventListener('transitionend', this._onTransitionEnd);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:updated', this._onCartUpdated);

    const closeBtn = this.$('[data-post-atc-close]');
    if (closeBtn) closeBtn.removeEventListener('click', this._onCloseClick);

    const viewCartBtn = this.$('[data-post-atc-view-cart]');
    if (viewCartBtn) viewCartBtn.removeEventListener('click', this._onViewCartClick);

    const recsContainer = this.$('.ab-post-atc__recs');
    if (recsContainer) recsContainer.removeEventListener('click', this._onRecAddClick);

    this.removeEventListener('transitionend', this._onTransitionEnd);

    this._clearAutoDismiss();
  }

  // ─── Event Handlers ─────────────────────────────

  _onCartUpdated(event) {
    const source = event.detail?.source;

    // Only show on primary ATC events from cart drawer (user adding from PDP)
    // Don't show for quantity changes, removals, or our own quick-adds
    if (source !== 'cart-drawer') return;

    // Don't re-show if already visible or already shown this page load
    if (this._isVisible || this._shownThisSession) return;

    // Don't show if no rec cards
    if (!this._recCards || this._recCards.length === 0) return;

    // Small delay to let cart drawer open animation start first
    setTimeout(() => this.show(), 600);
  }

  _onCloseClick(event) {
    event.preventDefault();
    this._dismissedByUser = true;
    this.hide();
  }

  _onViewCartClick(event) {
    event.preventDefault();
    this._dismissedByUser = true;
    this.hide();
    // Open cart drawer
    document.dispatchEvent(new CustomEvent('cart:open'));
  }

  _onRecAddClick(event) {
    const btn = event.target.closest('.ab-post-atc__rec-add');
    if (!btn) return;

    event.preventDefault();

    const variantId = parseInt(btn.dataset.variantId, 10);
    if (!variantId || this._isAdding) return;

    this._quickAdd(btn, variantId);
  }

  _onTransitionEnd(event) {
    // After hide transition completes, re-apply hidden attribute
    if (event.propertyName === 'transform' && !this._isVisible) {
      this.setAttribute('hidden', '');
      this.classList.remove('is-hiding');
    }
  }

  // ─── Show / Hide ───────────────────────────────

  show() {
    this._shownThisSession = true;
    this._isVisible = true;
    this._dismissedByUser = false;

    // Remove hidden to allow rendering, then trigger animation
    this.removeAttribute('hidden');

    // Force reflow before adding animation class
    this.offsetHeight; // eslint-disable-line no-unused-expressions

    this.classList.add('is-visible');

    // Auto-dismiss after 8 seconds
    this._startAutoDismiss();
  }

  hide() {
    if (!this._isVisible) return;

    this._isVisible = false;
    this._clearAutoDismiss();

    this.classList.remove('is-visible');
    this.classList.add('is-hiding');

    // If user didn't explicitly close, open cart drawer after hide
    if (!this._dismissedByUser) {
      setTimeout(() => {
        document.dispatchEvent(new CustomEvent('cart:open'));
      }, 350);
    }
  }

  _startAutoDismiss() {
    this._clearAutoDismiss();
    this._autoDismissTimer = setTimeout(() => {
      if (this._isVisible && !this._isAdding) {
        this.hide();
      }
    }, 8000);
  }

  _clearAutoDismiss() {
    if (this._autoDismissTimer) {
      clearTimeout(this._autoDismissTimer);
      this._autoDismissTimer = null;
    }
  }

  // ─── Quick Add ─────────────────────────────────

  async _quickAdd(btn, variantId) {
    this._isAdding = true;
    this._clearAutoDismiss();

    const originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding...';
    btn.classList.add('is-loading');

    try {
      window.__abCartDrawerAdding = true;

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: 1,
            properties: { '_rec_source': 'post_atc' }
          }]
        })
      });

      window.__abCartDrawerAdding = false;

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.description || `Add failed: ${response.status}`);
      }

      await response.json();

      // Show success state
      btn.textContent = 'Added!';
      btn.classList.remove('is-loading');
      btn.classList.add('is-added');

      // Dispatch cart:updated so header count and cart drawer refresh
      const cartResponse = await fetch('/cart.js', {
        headers: { 'Content-Type': 'application/json' }
      });
      const cart = await cartResponse.json();

      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cart, source: 'post-atc-modal' }
      }));

      // After brief pause, dismiss modal and open cart drawer
      setTimeout(() => {
        this._dismissedByUser = true;
        this.hide();
        document.dispatchEvent(new CustomEvent('cart:open'));
      }, 1000);

    } catch (err) {
      window.__abCartDrawerAdding = false;
      console.error('[ABPostAtcModal] Quick-add error:', err);

      btn.textContent = 'Error';
      btn.classList.remove('is-loading');
      btn.classList.add('is-error');

      setTimeout(() => {
        btn.disabled = false;
        btn.textContent = originalText;
        btn.classList.remove('is-error');
        this._startAutoDismiss();
      }, 2000);
    } finally {
      this._isAdding = false;
    }
  }
}

defineComponent('ab-post-atc-modal', ABPostAtcModal);
